import { buttonStyle } from 'components/buttons/buttonStyle';
import Loader from 'components/Loader';
import { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
    getClassifierCode,
    HistoryDto,
    HistoryEntityDto,
    HistoryForm,
    HistoryRecordState,
    UpdateHistoryDto,
} from 'types/histories';
import { XIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { DialogTitle } from 'components/ui/dialog';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { ModalFooterSimple } from '../ModalFooterSimple';
import { ModalHeader } from '../ModalHeader';
import { HistoryFormData, HistorySheet } from './HistoryModal';
import HistoryTableHeader from './HistoryModalHeader';
import HistoryRecord from './HistoryRecord';

interface Props {
    titleId?: string;
    fieldType: string;
    fieldName: string;
    activity: string;
    onClose: () => void;
    historyData: HistoryDto[] | undefined;
    handleUpdate: (
        record: HistoryEntityDto & UpdateHistoryDto & { entityType?: string; fieldType?: string }
    ) => Promise<void>;
    handleDelete?: (record: HistoryEntityDto) => Promise<void>;
    detached?: HistorySheet;
}

const HistoryModalContent: FC<Props> = ({
    titleId = 'history.modal.title',
    fieldType,
    fieldName,
    activity,
    onClose,
    historyData,
    handleUpdate,
    handleDelete,
    detached,
}) => {
    const defaultFormData = useMemo(
        () => (historyData ? mapHistoryDtoToHistoryFormData(historyData) : []),
        [historyData]
    );

    const { setValue, getValues } = useForm<HistoryFormData>({
        defaultValues: { histories: defaultFormData },
        values: { histories: defaultFormData },
    });

    const { isLoading: isLoadingClassifiers, allClassifiers } = useClassifiers({
        codes: ['MATTER_STATE_TYPE', 'SEGMENT_TYPE', 'SERVICE_LEVEL_TYPE'],
    });
    const classifierCode = getClassifierCode(fieldType);

    const hasUserEditPermission = useHasPermissions(['UPDATE.ENTITY_FIELD_HISTORY']);

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    const title = t(titleId, { fieldName, activity });
    return (
        <>
            {detached ? (
                <div className="border-divider flex items-start justify-between gap-2 border-b px-6 pt-5 pb-3">
                    <div className="min-w-0">
                        <DialogTitle className="text-foreground m-0 text-base font-semibold">{title}</DialogTitle>
                        {detached.subtitle ? (
                            <div className="text-muted-foreground mt-0.5 text-xs">{detached.subtitle}</div>
                        ) : null}
                    </div>
                    <Button variant="ghost" size="icon-sm" className="-mr-2" aria-label={t('close')} onClick={onClose}>
                        <XIcon className="size-4" />
                    </Button>
                </div>
            ) : (
                <ModalHeader title={title} onCancel={onClose} />
            )}
            <div className="h-full overflow-y-auto">
                {detached?.description ? (
                    <p className="text-muted-foreground m-0 px-6 pt-4 text-sm">{detached.description}</p>
                ) : null}
                <HistoryTableHeader />
                <div className="flex flex-col gap-4 py-4">
                    {getValues('histories').map((history, index) => {
                        return (
                            <HistoryRecord
                                key={history.id}
                                index={index}
                                history={history}
                                setValue={setValue}
                                classifierCode={classifierCode}
                                allClassifiers={allClassifiers}
                                hasUserEditPermission={hasUserEditPermission}
                                isLatestRecord={index === 0}
                                handleUpdate={handleUpdate}
                                handleDelete={handleDelete}
                                allHistories={getValues('histories')}
                            />
                        );
                    })}
                </div>
            </div>
            {detached ? (
                <div className="border-divider flex justify-end border-t px-6 py-3">
                    <Button variant="secondary" className="h-9 px-3.5 font-semibold" onClick={onClose}>
                        <FormattedMessage id="close" />
                    </Button>
                </div>
            ) : (
                <ModalFooterSimple
                    buttons={[
                        <Button
                            key="cancel"
                            {...buttonStyle({ color: 'primary', variant: 'outlined' })}
                            onClick={onClose}
                        >
                            <FormattedMessage id="close" />
                        </Button>,
                    ]}
                />
            )}
        </>
    );
};

const mapHistoryDtoToHistoryFormData = (historyDtos: HistoryDto[]): HistoryForm[] => {
    return historyDtos.map((historyDto) => {
        return {
            id: historyDto.id,
            entityId: historyDto.entityId,
            entityType: historyDto.entityType,
            fieldType: historyDto.fieldType,
            fieldValue: historyDto.fieldValue,
            startDate: historyDto.startDate,
            endDate: historyDto.endDate,
            recordState: HistoryRecordState.VIEWING,
        };
    });
};

export default HistoryModalContent;
